/**
 * React Hook for Google Analytics User Tracking
 * Automatically initializes GA4 user_id tracking when user logs in
 * 
 * This hook:
 * 1. Checks if user is authenticated (has valid access token)
 * 2. Checks Redux for cached user ID
 * 3. If not cached, fetches from /user/me API and caches in Redux
 * 4. Sets GA4 user_id for cross-session tracking with numeric ID
 * 5. Clears user_id on logout 
 */

import { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { selectUserId, selectAccessToken, setUserId } from '../redux/uiSlice';
import { setGAUserId, clearGAUserId } from '../analytics';
import { isTokenExpired } from '../utils/verifyJwt';
import { getUserProfile } from '../../api/userApi';


export const useGAUserTracking = () => {
  const dispatch = useAppDispatch();
  const storedUserId = useAppSelector(selectUserId);
  const accessToken = useAppSelector(selectAccessToken);

  useEffect(() => {
    // Only run on client side
    if (typeof window === 'undefined') return;

    const initializeTracking = async () => {
      try {
        // Get access token from Redux or localStorage
        const token = accessToken || localStorage.getItem('access-token');

        // If no token or token expired, clear GA user tracking
        if (!token || isTokenExpired(token)) {
          clearGAUserId();
          dispatch(setUserId(null));
          return;
        }

        // If user ID is already in Redux, use it
        if (storedUserId) {
          setGAUserId(storedUserId.toString());
          return;
        }

        // Otherwise, fetch user profile 
        try {
          const userProfile = await getUserProfile();
          
          if (userProfile && userProfile.id) {
            
            dispatch(setUserId(userProfile.id));
            setGAUserId(userProfile.id.toString());
          } else {
            // console.warn('[GA4 Hook] User profile missing ID field');
          }
        } catch (error) {
          // Silently fail if API call fails (e.g., network error, auth error)
        //   console.error('[GA4 Hook] Failed to fetch user profile:', error);
        }
      } catch (error) {
        // Fail silently - never interrupt user experience
        // console.error('[GA4 Hook] Error initializing user tracking:', error);
      }
    };

    initializeTracking();
  }, [storedUserId, accessToken, dispatch]);

  // Listen for storage changes (e.g., logout in another tab)
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleStorageChange = (e: StorageEvent) => {
      try {
        // If access token was removed (logout), clear GA tracking
        if (e.key === 'access-token' && !e.newValue) {
          clearGAUserId();
          dispatch(setUserId(null));
        }
        // If access token was added/changed (login), trigger re-initialization
        // The main effect will handle fetching the user profile
      } catch (error) {
        // Fail silently
        // console.error('[GA4 Hook] Storage event error:', error);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [dispatch]);
};


export const initializeGAUserTracking = (userId: number, dispatch: any): void => {
  try {
    if (!userId || typeof userId !== 'number') {
    //   console.warn('[GA4] Invalid user ID provided:', userId);
      return;
    }

    // Store in Redux
    dispatch(setUserId(userId));
    
    // Set GA4 user_id
    setGAUserId(userId.toString());
  } catch (error) {
    // console.error('[GA4] Manual initialization failed:', error);
  }
};
